import {
  SignatureInfo,
  signEIP2612Permit,
} from '@arcxgame/contracts/dist/src/utils/signEIP2612Permit'
import { LiquidityCampaignV2 } from '@arcxgame/contracts/dist/src/typings'
import { BigNumber, Contract, ContractTransaction, Signer } from 'ethers'
import { JsonRpcSigner } from '@ethersproject/providers/lib'
import { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import { Network } from 'src/config'
import ArcFarm from './ArcFarm'
import { getReadableNumber } from 'src/helpers/getReadableNumber'
import { IRewardPair } from './IRewardPair'

export class LiquidityCampaignV2Farm extends ArcFarm {
  constructor(
    name: string,
    platform: string,
    pair: string,
    pairLink: string,
    note: string,
    signer: Signer,
    network: Network,
    public contract: LiquidityCampaignV2,
    client: ApolloClient<NormalizedCacheObject>,
  ) {
    super(name, platform, pair, pairLink, note, signer, network, contract, client)
  }

  /* ========== Getters ========== */

  hasDebtRequirement(): boolean {
    return false
  }

  async getAvailableToStake(): Promise<BigNumber> {
    const stakingToken = await this.getStakingToken()

    const stakableBalance = await stakingToken.balanceOf(await this.signer.getAddress())

    return stakableBalance
  }

  async getStakedBalance(): Promise<BigNumber> {
    try {
      return await super.getStakedBalance()
    } catch (err) {
      console.error(
        'Error calling contract.balanceOf(address). This is probably farm 6. Trying .balanceOfStaker',
      )
    }

    try {
      const contract = new Contract(
        this.contract.address,
        ['function balanceOfStaker(address staker) view returns(uint256)'],
        this.signer,
      )
      return await contract.balanceOfStaker(await this.signer.getAddress())
    } catch (err) {
      console.error('Error calling contract.balanceOfStaker(address)', err)
    }

    return BigNumber.from(0)
  }

  async getEarnings(): Promise<IRewardPair[]> {
    const signerAddress = await this.signer.getAddress()
    const arcEarned = await this.contract.earned(signerAddress)

    return [
      {
        symbol: 'ARCX',
        amount: arcEarned,
      },
    ]
  }

  async getEarningsString(): Promise<string> {
    const [rewardEarned] = await this.getEarnings()

    return `${getReadableNumber(rewardEarned.amount)} ${rewardEarned.symbol}`
  }

  /* ========== Modifiers ========== */

  async stake(amount: BigNumber): Promise<ContractTransaction> {
    const signerAddy = await this.signer.getAddress()
    const stakingToken = await this.getStakingToken()
    const allowance = await stakingToken.allowance(
      await this.signer.getAddress(),
      this.contract.address,
    )
    const nonce = await stakingToken.nonces(signerAddy)

    if (allowance.lt(amount)) {
      try {
        let signature: SignatureInfo

        try {
          signature = await signEIP2612Permit(
            this.signer as JsonRpcSigner,
            stakingToken.address,
            await this.signer.getAddress(),
            this.contract.address,
            amount,
            BigNumber.from(Date.now() + 60 * 10),
            nonce,
            await this.signer.getChainId(),
            '1',
          )
        } catch (err) {
          console.error(err)
          console.log(
            'There was an error signing the permit. Switching to traditional approve - stake method.',
          )

          return this._stakeWithApprove(amount)
        }

        const stakeTx = await this.contract.stakeWithPermit(
          amount,
          signature.deadline,
          signature.v,
          signature.r,
          signature.s,
        )
        return stakeTx
      } catch (err) {
        console.error(err)
        throw new Error('An error occured during staking')
      }
    } else {
      // The user already allowed
      return this.contract.stake(amount)
    }
  }

  private async _stakeWithApprove(amount: BigNumber): Promise<ContractTransaction> {
    const stakingToken = await this.getStakingToken()

    try {
      const tx = await stakingToken.approve(this.contract.address, amount.toString())
      await tx.wait()
    } catch {
      // If an approval is failing it's probably because it's a curve token LP token wanting
      // the approval to be set to 0 smh...
      const resetApproval = await stakingToken.approve(this.contract.address, 0)
      await resetApproval.wait()

      const approveTx = await stakingToken.approve(this.contract.address, amount.toString())
      await approveTx.wait()
    }

    try {
      const stakeTx = await this.contract.stake(amount)
      return stakeTx
    } catch (err) {
      console.error(err)
      throw Error('An error occured during staking')
    }
  }
}
